import { RefObject } from 'preact';
import { useEffect, useCallback, useRef, useMemo } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { Keys } from '../utils/UNSAFE_keys';
import { DataState, Metadata, Range, SelectionDetail } from '../UNSAFE_Collection';
import { getListViewRoles } from './ListRoles';
import { SelectionRangeDetail, useSelection } from '../hooks/PRIVATE_useSelection';
import { useCurrentKey } from '../hooks/PRIVATE_useCurrentKey';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { useCollectionGestureContext } from '../hooks/PRIVATE_useCollectionGestureContext';
import { useItemAction } from '../hooks/PRIVATE_useItemAction';
import { Props as ListViewProps } from '../UNSAFE_ListView';
import { getPrevNextKey, findElementByKey, keyExtractor } from '../utils/PRIVATE_collectionUtils';
import { ITEM_SELECTOR } from './ListItem';
import { scrollToVisible } from './ListUtils';

export const useSelectionAndNavigation = <K extends string | number, D>(
  currentKey: ListViewProps<K, D>['currentKey'],
  selectedKeys: Keys<K>,
  selectionMode: ListViewProps<K, D>['selectionMode'] = 'none',
  onCurrentKeyChange: ListViewProps<K, D>['onCurrentKeyChange'],
  onSelectionChange: ListViewProps<K, D>['onSelectionChange'],
  onItemAction: ListViewProps<K, D>['onItemAction'],
  data: ListViewProps<K, D>['data'],
  viewportConfig: ListViewProps<K, D>['viewportConfig'],
  role: ListViewProps<K, D>['role'] = 'grid',
  scrollToVisibleOffset: ListViewProps<K, D>['scrollToVisibleOffset'],
  rootRef: RefObject<HTMLDivElement>,
  pendingSelectionCallback: (detail: SelectionRangeDetail<K>, value: Range) => void
) => {
  const listRoles = useMemo(() => getListViewRoles(role), [role]);

  // tracking the anchor key which is used for shift+click selection
  const anchorKey = useRef<K | undefined>();

  const { currentKeyProps } = useCurrentKey(
    (element) => keyExtractor(element, ITEM_SELECTOR),
    selectionMode !== 'multiple',
    getPrevNextKey(rootRef.current, currentKey, true, ITEM_SELECTOR),
    getPrevNextKey(rootRef.current, currentKey, false, ITEM_SELECTOR),
    undefined,
    undefined,
    currentKey,
    onCurrentKeyChange
  );

  const [showFocusRing, focusRingProps] = useCollectionFocusRing(rootRef, ['ArrowUp', 'ArrowDown']);

  useEffect(() => {
    if (currentKey != null && rootRef.current) {
      const elem = findElementByKey(rootRef.current, currentKey, ITEM_SELECTOR);
      if (elem) {
        const activeDescendant = rootRef.current.getAttribute('aria-activedescendant');
        const cell = elem.querySelector(`[role=${listRoles.cell}]`);
        if (cell && activeDescendant !== cell.id) {
          // update aria-activedescendant for screenreader
          rootRef.current.setAttribute('aria-activedescendant', cell.id);

          const scroller = viewportConfig?.scroller();
          const offset = scrollToVisibleOffset ? scrollToVisibleOffset(rootRef.current) : 0;
          // make sure item is visible
          scrollToVisible(elem, scroller != null ? scroller : rootRef.current, offset);
        }
      }
    }
    // listen to data because the current item would re-render when the virtualizer
    // is fetching data or updating the data, i.e. the id of current item would be updated,
    // so the currentKey itself couldn't guarantee the root has latest aria-activedescendant
  }, [currentKey, data, listRoles.cell, viewportConfig, scrollToVisibleOffset, rootRef]);

  const handleSelectionChange = useCallback(
    (detail: SelectionDetail<K>) => {
      if (onSelectionChange) {
        if (detail.value.all === false && detail.value.keys.size > 0) {
          // during regular selection, the last key selected by a user gesture is the anchor key
          anchorKey.current = Array.from(detail.value.keys.values()).pop();
        }
        onSelectionChange(detail);
      }
    },
    [anchorKey, onSelectionChange]
  );

  const handleSelectionRangeChange = useCallback(
    (detail: SelectionRangeDetail<K>) => {
      if (data && onSelectionChange) {
        const value = handleSelectionRange(detail, data);
        if (Array.isArray(value)) {
          if (detail.eventType === 'shiftSpace') {
            // during range selection by shift+space, the first key selected by a user gesture is the anchor key
            anchorKey.current = detail.value.start;
          } else {
            // during range selection by shift+click/arrow keys, the last key selected by a user gesture is the anchor key
            anchorKey.current = detail.value.end;
          }
          onSelectionChange({ value: { all: false, keys: new Set(value) }, target: null });
        } else {
          pendingSelectionCallback(detail, value);
        }
      }
    },
    [data, onSelectionChange, pendingSelectionCallback]
  );

  const value = useCollectionGestureContext();

  const { selectionProps } = useSelection(
    (element: HTMLElement) => {
      if (element === rootRef.current) {
        return currentKey === undefined ? null : currentKey;
      }
      return keyExtractor(element, ITEM_SELECTOR) as K;
    },
    selectedKeys,
    selectionMode,
    false,
    'replace',
    value === 'embedded',
    handleSelectionChange,
    anchorKey.current,
    currentKey,
    (currentKey: K | undefined, isPrev: boolean) =>
      getPrevNextKey(rootRef.current, currentKey, isPrev, ITEM_SELECTOR),
    undefined,
    (key: K) => {
      if (rootRef.current) {
        const elem = findElementByKey(rootRef.current, key, ITEM_SELECTOR);
        if (elem) {
          const scroller = viewportConfig?.scroller();
          const offset = scrollToVisibleOffset ? scrollToVisibleOffset(rootRef.current) : 0;
          scrollToVisible(elem, scroller != null ? scroller : rootRef.current, offset);
        }
      }
    },
    handleSelectionRangeChange
  );

  const itemActionProps = useItemAction(
    currentKey,
    data,
    onItemAction,
    ITEM_SELECTOR,
    value === 'embedded'
  );

  return {
    eventProps: mergeProps(currentKeyProps, focusRingProps, selectionProps, itemActionProps),
    showFocusRing,
    handleSelectionChange
  };
};

export const handleSelectionRange = <K, D>(
  detail: SelectionRangeDetail<K>,
  dataState: DataState<K, D>
) => {
  const keys = dataState.data.map((value: { data: D; metadata: Metadata<K> }) => {
    return value.metadata.key;
  });
  const startIndex = keys.indexOf(detail.value.start);
  const endIndex = keys.indexOf(detail.value.end);
  const minIndex = Math.min(startIndex, endIndex);
  const maxIndex = Math.max(startIndex, endIndex);
  if (minIndex === -1) {
    // return a range to fetch so we can find all the keys, maxIndex should not be -1
    return { offset: 0, count: dataState.totalSize };
  } else {
    // range of keys are in the current viewport, return them
    return keys.slice(minIndex, maxIndex + 1);
  }
};
